import React, { useState } from "react";
import "./WerkenBijContact.css";
import { sendInterestEmail } from "../../services/emailService";

const WerkenBijContact = () => {
  const [formData, setFormData] = useState({
    voornaam: '',
    achternaam: '',
    email: '',
    telefoon: '',
    bericht: '',
    cv: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState(''); // 'success' or 'error'
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    // Validate file type and size
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!allowedTypes.includes(file.type)) {
        setValidationErrors(prev => ({
          ...prev,
          cv: 'Alleen PDF, DOC en DOCX bestanden zijn toegestaan'
        }));
        return;
      }
      
      if (file.size > maxSize) {
        setValidationErrors(prev => ({
          ...prev,
          cv: 'Bestand mag maximaal 5MB groot zijn'
        }));
        return;
      }
      
      setFormData({
        ...formData,
        cv: file
      });
      
      // Clear validation error
      if (validationErrors.cv) {
        setValidationErrors(prev => ({
          ...prev,
          cv: ''
        }));
      }
    }
  };

  const removeFile = () => {
    setFormData({
      ...formData,
      cv: null
    });
    // Reset file input
    const fileInput = document.getElementById('cv');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.voornaam.trim()) {
      errors.voornaam = 'Voornaam is verplicht';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is verplicht';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Voer een geldig email adres in';
    }
    
    if (!formData.telefoon.trim()) {
      errors.telefoon = 'Telefoon is verplicht';
    }
    
    if (!formData.cv) {
      errors.cv = 'CV is verplicht';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      const result = await sendInterestEmail(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage(result.message);
        // Reset form na succesvol verzenden
        setFormData({
          voornaam: '',
          achternaam: '',
          email: '',
          telefoon: '',
          bericht: '',
          cv: null
        });
        // Reset file input
        const fileInput = document.getElementById('cv');
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Er is een onverwachte fout opgetreden. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="werken-bij-contact-section">
      <div className="contact-container">
        <div className="contact-content">
          <h2 className="contact-title">Interesse?</h2>
          
          <div className="contact-description">
            <p>
              Stuur jouw motivatie en CV via het onderstaande formulier 
              en wie weet verwelkomen wij jou binnenkort in ons team.
            </p>
            <p>
              Samen maken we van elke kraamtijd een warme, ontspannen en onvergetelijke periode.
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate encType="multipart/form-data">
            {submitMessage && (
              <div className={`submit-message ${submitStatus}`}>
                {submitMessage}
              </div>
            )}
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="voornaam">Voornaam <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="voornaam"
                  name="voornaam"
                  value={formData.voornaam}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={validationErrors.voornaam ? 'error' : ''}
                />
                {validationErrors.voornaam && (
                  <span className="error-message">{validationErrors.voornaam}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="achternaam">Achternaam</label>
                <input
                  type="text"
                  id="achternaam"
                  name="achternaam"
                  value={formData.achternaam}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email <span className="required-asterisk">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={validationErrors.email ? 'error' : ''}
                />
                {validationErrors.email && (
                  <span className="error-message">{validationErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="telefoon">Telefoon <span className="required-asterisk">*</span></label>
                <input
                  type="tel"
                  id="telefoon"
                  name="telefoon"
                  value={formData.telefoon}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={validationErrors.telefoon ? 'error' : ''}
                />
                {validationErrors.telefoon && (
                  <span className="error-message">{validationErrors.telefoon}</span>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label htmlFor="cv">CV <span className="required-asterisk">*</span></label>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="cv"
                  name="cv"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="file-input"
                  accept=".pdf,.doc,.docx"
                />
                <label htmlFor="cv" className="file-upload-label">
                  <span className="file-upload-icon">📎</span>
                  <span className="file-upload-text">
                    {formData.cv ? formData.cv.name : 'Klik hier om je CV te uploaden (PDF, DOC, DOCX)'}
                  </span>
                </label>
                {formData.cv && (
                  <div className="file-info">
                    <span className="file-size">{formatFileSize(formData.cv.size)}</span>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="remove-file-btn"
                      disabled={isSubmitting}
                      aria-label="Verwijder bestand"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              {validationErrors.cv && (
                <span className="error-message">{validationErrors.cv}</span>
              )}
              <span className="file-help-text">Maximaal 5MB. Toegestane formaten: PDF, DOC, DOCX</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="bericht">Motivatie</label>
              <textarea
                id="bericht"
                name="bericht"
                value={formData.bericht}
                onChange={handleChange}
                rows="5"
                disabled={isSubmitting}
                placeholder="Vertel ons waarom je bij Kraamzorg Bee wilt werken..."
              ></textarea>
            </div>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Verzenden...' : 'Sollicitatie Versturen'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default WerkenBijContact; 